import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    onAuthStateChanged,
    signOut
} from 'firebase/auth';
import { 
    getFirestore, 
    setLogLevel, 
    doc, 
    getDoc, 
    setDoc 
} from 'firebase/firestore';

// --- Importações de Configuração ---
import { firebaseConfig, appId } from './config/firebaseConfig';
import { ADMIN_EMAIL, PERMISSIONS_CONFIG } from './config/permissionsConfig';
import { ALL_PAGE_KEYS } from './config/menuConfig';

// --- Importação dos componentes de layout e login ---
import LoginPage from './pages/LoginPage';
import AppLayout from './components/AppLayout';
import PublicTechnicalReportPage from './pages/PublicTechnicalReportPage';
import ClientPortalLayout from './pages/ClientPortalLayout';
import OrientationGuard from './components/common/OrientationGuard';

import { getDatabase } from 'firebase/database';
import { getStorage } from 'firebase/storage';

// --- Configuração Firebase ---
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const dbAsset = getFirestore(app, "arcusasset");
export const dbFinanc = getFirestore(app, "financ"); // Módulo Financeiro
export const rtdb = getDatabase(app, "https://intranet-arcus-default-rtdb.firebaseio.com");
export const storage = getStorage(app);
setLogLevel('error');

export default function App() {
    const [user, setUser] = useState(null);
    const [portalUser, setPortalUser] = useState(() => {
        try {
            const saved = localStorage.getItem('arcus_portal_user');
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            return null;
        }
    });
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [currentPage, setCurrentPage] = useState('home');
    const [navTrigger, setNavTrigger] = useState(0);

    // Detectar modo público via URL
    const isPublicLaudo = window.location.search.includes('public=laudo');

    // Função de navegação centralizada
    const navigateTo = (page) => {
        setCurrentPage(page);
        setNavTrigger(prev => prev + 1);
    };

    // LISTA DE CHAVES TÉCNICAS (Devem ser iguais às do MainContent.js)
    // Isso garante que o Admin sempre tenha 'edit' em tudo o que existir
    const TECHNICAL_PAGES = ALL_PAGE_KEYS; // fonte única: src/config/menuConfig.js

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            // Verifica se o usuário atual é do Portal Web
            const savedPortal = localStorage.getItem('arcus_portal_user');
            if (savedPortal) {
                try {
                    const parsed = JSON.parse(savedPortal);
                    if (parsed && parsed.isPortalUser) {
                        setUser(user);
                        setPortalUser(parsed);
                        setLoadingAuth(false);
                        
                        // Atualiza as permissões em background toda vez que a página recarregar
                        if (parsed.id) {
                            getDoc(doc(db, `/artifacts/${appId}/public/data/portal_users`, parsed.id)).then(snap => {
                                if (snap.exists()) {
                                    const freshData = snap.data();
                                    const freshUser = { ...parsed, ...freshData, isPortalUser: true };
                                    localStorage.setItem('arcus_portal_user', JSON.stringify(freshUser));
                                    setPortalUser(freshUser);
                                }
                            }).catch(() => {});
                        }
                        
                        return;
                    }
                } catch (e) {}
            }

            setUser(user);
            
            if (user) {
                localStorage.removeItem('arcus_portal_user');
                setPortalUser(null);

                const permissionsDocRef = doc(db, `/artifacts/${appId}/public/data/user_permissions`, user.uid);
                const permissionsDocSnap = await getDoc(permissionsDocRef);

                // 1. LOGICA PARA O ADMIN (JONATHAN)
                // Garante que tu tenhas 'edit' em todas as páginas da lista TECHNICAL_PAGES
                if (user.email === ADMIN_EMAIL) {
                    const currentPermissions = permissionsDocSnap.exists() ? permissionsDocSnap.data().permissions : {};
                    const updatedPermissions = { ...currentPermissions };
                    let hasChanges = false;

                    TECHNICAL_PAGES.forEach(pageKey => {
                        if (updatedPermissions[pageKey] !== 'edit') {
                            updatedPermissions[pageKey] = 'edit';
                            hasChanges = true;
                        }
                    });

                    if (hasChanges || !permissionsDocSnap.exists()) {
                        await setDoc(permissionsDocRef, {
                            userId: user.uid,
                            email: user.email,
                            permissions: updatedPermissions
                        }, { merge: true });
                    }
                } 
                // 2. LOGICA PARA NOVOS UTILIZADORES
                // Se o utilizador não tem documento, cria um básico (só leitura na home)
                else if (!permissionsDocSnap.exists()) {
                    const defaultPermissions = {};
                    TECHNICAL_PAGES.forEach(key => {
                        defaultPermissions[key] = key === 'home' ? 'read' : 'none';
                    });

                    await setDoc(permissionsDocRef, {
                        userId: user.uid,
                        email: user.email,
                        permissions: defaultPermissions
                    });
                }
            }
            
            setLoadingAuth(false);
        });
        return () => unsubscribe();
    }, []);

    // Renderização para Modo Público
    if (isPublicLaudo) {
        return <PublicTechnicalReportPage />;
    }

    if (loadingAuth && !portalUser) {
        return (
            <div className="flex items-center justify-center h-screen bg-slate-900 text-cyan-400 font-sans font-bold italic animate-pulse">
                A sincronizar sistema Arcus...
            </div>
        );
    }

    const handlePortalLogout = () => {
        localStorage.removeItem('arcus_portal_user');
        setPortalUser(null);
        signOut(auth).catch(() => {});
    };

    const handlePortalLoginSuccess = (session) => {
        setPortalUser(session);
    };

    return (
        <>
            <OrientationGuard />
            {portalUser ? (
                <ClientPortalLayout 
                    portalUser={portalUser} 
                    onLogout={handlePortalLogout} 
                />
            ) : user ? (
                <AppLayout 
                    user={user} 
                    currentPage={currentPage} 
                    navigateTo={navigateTo} 
                    navTrigger={navTrigger}
                />
            ) : (
                <LoginPage onPortalLoginSuccess={handlePortalLoginSuccess} />
            )}
        </>
    );
}
